import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material';
import axios from 'axios';
import { MESSAGES } from '../common/messages';

import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/AddOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { enqueueSnackbar } from 'notistack';

const EditTrainerCheckItems = (props) => {
  const { typography, palette } = useTheme();
  const MAX_ITEM_COUNT = 10;

  // 指導者チェック問題を保持する変数
  const [trainerCheckItems, setTrainerCheckItems] = useState([]);

  // 末尾に指導者チェック問題を追加
  const addItem = () => {
    const copied = trainerCheckItems.concat();
    copied.push({
      key: Math.random(),
      text: '',
      serialNumber: trainerCheckItems.length,
    });
    setTrainerCheckItems(copied);
  };

  const styles = {
    main: {
      display: 'flex',
      padding: '0px 24px',
      flexDirection: 'column',
      alignItems: 'flex-start',
      alignSelf: 'stretch',

      borderRadius: '8px',
      border: '1px solid ' + palette.divider,
      background: palette.white,
      boxShadow: '2px 2px 2px 0px rgba(0, 0, 0, 0.03)',
      margin: '0px auto 0px auto',
    },
    backButton: {
      ...typography.body14bold,
      display: 'flex',
      width: '232px',
      height: '44px',
      padding: '0px 16px',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '8px',
      margin: '0px 8px 0px 8px',

      borderRadius: '100px',
      border: '1px solid ' + palette.primary.light,
      background: palette.white,
      color: palette.primary.light,
    },
    updateButton: {
      ...typography.body14bold,
      'width': '232px',
      'height': '44px',
      'padding': '0px 16px',
      'justifyContent': 'center',
      'alignItems': 'center',
      'gap': '8px',
      'margin': '0px 8px 0px 8px',

      'borderRadius': '100px',
      'border': '1px solid ' + palette.primary.light,
      'background': palette.primary.light,
      'color': palette.white,
      '&:hover': {
        width: '232px',
        height: '44px',
        padding: '0px 16px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '8px',
        margin: '0px 8px 0px 8px',
        borderRadius: '100px',
        border: '1px solid ' + palette.primary.light,
        background: palette.primary.light,
        color: palette.white,
      },
      '&:disabled': {
        ...typography.body14bold,
        border: '1px solid ' + palette.disabledCccccc,
        color: palette.white,
        background: palette.disabledCccccc,
      },
    },
    addItemButton: {
      ...typography.body14bold,
      width: '252px',
      height: '44px',
      backgroundColor: 'white',
      color: palette.primary.main,
      border: '1px solid ' + palette.primary.main,
      margin: '44px 0px',
    },
  };

  useEffect(() => {
    const fetchTrainerCheckItems = async () => {
      try {
        const result = await axios.get('/v1/trainer-check-items');
        const data = result.data.map((e) => {
          const item = {
            serialNumber: e.serialNumber,
            text: e.text,
            key: Math.random(),
          };
          return item;
        });
        if (data.length === 0) {
          addItem();
        } else {
          setTrainerCheckItems(data);
        }
      } catch (err) { }
    };
    fetchTrainerCheckItems();
  }, []);

  // 指導者チェック項目を一問を削除する関数
  const removeTrainerCheckItem = (serialNumber) => {
    const copied = trainerCheckItems.concat();
    for (let i = serialNumber; i < copied.length - 1; i++) {
      copied[i] = copied[i + 1];
      copied[i].serialNumber = i;
    }
    copied.pop();
    setTrainerCheckItems(copied);
  };

  // 未入力の項目があるか
  const isInsufficient = (items) => {
    if (items.some((item) => item.text.length === 0)) {
      return true;
    }
    return false;
  };

  const handleSubmit = async () => {
    if (isInsufficient(trainerCheckItems)) {
      enqueueSnackbar(MESSAGES.INSUFFICIENT, { variant: 'error' });
      return;
    }

    // 登録APIを呼び出し
    try {
      await axios.post('/v1/trainer-check-items', trainerCheckItems);
      enqueueSnackbar(MESSAGES.TRAINER_CHECK_ITEMS_UPDATED);
      props.handleMoveToTrainingResult();
    } catch (err) { }
  };

  return (
    <Container sx={{ margin: '16px auto' }}>
      <Stack direction='row' sx={styles.main}>
        <Typography variant='head20' margin='24px 0px 0px 0px' >指導者チェック項目の編集</Typography>
        {trainerCheckItems.map((e) => {
          return (
            <TrainerCheckItemComponent
              key={e.key}
              trainerCheckItem={e}
              checkItemNumber={e.serialNumber + 1}
              setTrainerCheckItem={(input) => {
                const copied = trainerCheckItems.filter((item) => item.serialNumber !== e.serialNumber);
                copied.push(input);
                copied.sort((a, b) => a.serialNumber - b.serialNumber);
                setTrainerCheckItems(copied);
              }}
              removeTrainerCheckItem={removeTrainerCheckItem}
              numberOfItems={trainerCheckItems.length}
            />
          );
        })}

        <Button variant="outlined"
          sx={styles.addItemButton}
          startIcon={<AddIcon />}
          onClick={() => {
            addItem();
          }}
          disabled={trainerCheckItems.length === MAX_ITEM_COUNT}
        >
          項目を追加する（残り{MAX_ITEM_COUNT - trainerCheckItems.length}項目）
        </Button>

        <Grid
          container
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction={'row'} sx={{ width: 'fit-content', margin: '0px auto 24px auto' }}>
            <Button variant="outlined"
              sx={styles.backButton}
              onClick={() => {
                props.handleMoveToTrainingResult();
              }}
            >
              <p>トレーニング結果一覧に戻る</p>
            </Button>
            <Button variant="filled"
              sx={styles.updateButton}
              disabled={isInsufficient(trainerCheckItems)}
              onClick={handleSubmit}
            >
              <p>指導者チェック項目を更新</p>
            </Button>
          </Stack>
        </Grid>
      </Stack>
    </Container>
  );
};

const TrainerCheckItemComponent = (props) => {
  const { typography, palette } = useTheme();

  const styles = {
    main: {
      display: 'flex',
      padding: '24px',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: '36px',
      alignSelf: 'stretch',

      borderRadius: '8px',
      border: '1px solid ' + palette.divider,
      background: palette.white,
      boxShadow: '2px 2px 2px 0px rgba(0, 0, 0, 0.03)',
    },
    deleteButton: {
      display: 'flex',
      height: '44px',
      alignItems: 'center',
      gap: '4px',

      width: '154px',
      padding: '0px 0px 0px 0px',
      alignItems: 'center',
      margin: '24px 0px 0px 0px',

      /* Style */
      borderRadius: '8px',
      ...typography.body14bold,
    },
    deleteIcon: {
      ...typography.body14,
      display: 'flex',
      width: '24px',
      height: '24px',
      justifyContent: 'center',
      alignItems: 'center',
    },
  };

  return (
    <Stack sx={{ width: '100%' }}>
      <Typography variant='body16bold' margin='44px 0px 0px 0px'>項目{props.checkItemNumber}</Typography>
      <TextField variant="outlined"
        placeholder="指導者チェック内容"
        InputProps={{
          style: {
            backgroundColor: 'white',
          },
        }}
        multiline
        maxRows={3}
        defaultValue={props.trainerCheckItem.text}
        onChange={(event) => {
          const copied = structuredClone(props.trainerCheckItem);
          copied.text = event.target.value;
          props.setTrainerCheckItem(copied);
        }}
      />
      {props.numberOfItems > 1 && <Button variant="outlined" color='error'
        sx={styles.deleteButton}
        startIcon={<DeleteIcon sx={styles.deleteIcon} />}
        onClick={() => {
          // button click action
          props.removeTrainerCheckItem(props.trainerCheckItem.serialNumber);
        }}
      >
        項目を削除する
      </Button>}
    </Stack>
  );
};

export default EditTrainerCheckItems;


EditTrainerCheckItems.propTypes = {
  handleMoveToTrainingResult: PropTypes.func,
};

TrainerCheckItemComponent.propTypes = {
  checkItemNumber: PropTypes.number,
  removeTrainerCheckItem: PropTypes.func,
  trainerCheckItem: PropTypes.object,
  setTrainerCheckItem: PropTypes.func,
  numberOfItems: PropTypes.number,
};
